import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import ModalContainer from "../common/ModalContainer";
import { Modal3 } from "../DIsplay/Modal/Modal3";
import Button1 from "../form/Button1";
import { useInsuranceApi } from "../../hooks/useInsuranceApi";
import { setInsuranceOrders } from "../../redux/reducers/orderSlice";
import { formatMoney } from "../../features/utils/formatMoney";
import RebookInsuranceDetailsForm from "./RebookInsuranceDetailsForm";

const DEFAULT_PERSON = {
  firstName: "Chinedu",
  middleName: "Kerian",
  lastName: "Ike",
  gender: "Male",
  title: "Mr",
  city: "Lagos",
  state: "Lagos",
  passportNumber: "18277872A4",
  phone: "08034635316",
  countryCode: "234",
  birthDate: "1990-10-20",
  email: "admin-dembe@yopmail.com",
};

const DEFAULT_FLIGHT = {
  flightNumber: "2992AAD",
  pnr: "FEDSAA",
  airline: "ET",
  departureAirport: "ADD",
  arrivalAirport: "LOS",
};

function getRequestBody(booking) {
  return (
    booking?.orderData?.orderData?.requestBody ||
    booking?.booking?.orderData?.orderData?.requestBody ||
    booking?.orderData?.requestBody ||
    null
  );
}

function formatSearchDate(value) {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() ? date.format("YYYY-MM-DD") : "";
}

function countPersonsByAge(persons, category) {
  return (persons || []).filter(
    (person) =>
      String(person?.ageCategory || "").trim().toLowerCase() === category,
  ).length;
}

function buildInsurancePersonInfo() {
  return { ...DEFAULT_PERSON };
}

function buildDefaultFlight({
  departureDate,
  arrivalDate,
  segmentIndex = 0,
  isReturn = false,
}) {
  return {
    departureDate,
    arrivalDate,
    flightNumber: DEFAULT_FLIGHT.flightNumber,
    pnr: DEFAULT_FLIGHT.pnr,
    airline: DEFAULT_FLIGHT.airline,
    departureAirport: isReturn ? "LOS" : DEFAULT_FLIGHT.departureAirport,
    arrivalAirport: isReturn ? "ADD" : DEFAULT_FLIGHT.arrivalAirport,
    segmentIndex,
  };
}

function buildDocInsuranceSearchRequest(booking, requestBodyOverride) {
  const requestBody = requestBodyOverride || getRequestBody(booking);
  const persons = requestBody?.personsInfo || [];
  const startDate = formatSearchDate(
    requestBody?.startDate ||
      requestBody?.departureDate ||
      booking?.startDate ||
      requestBody?.flights?.[0]?.departureDate,
  );
  const endDate = formatSearchDate(
    requestBody?.endDate ||
      requestBody?.arrivalDate ||
      booking?.endDate ||
      requestBody?.flights?.[requestBody?.flights?.length - 1]?.arrivalDate ||
      requestBody?.flights?.[0]?.arrivalDate,
  );

  if (!startDate || !endDate) return null;

  const orderFlights = Array.isArray(requestBody?.flights)
    ? requestBody.flights
    : [];
  const isRoundTrip = Boolean(requestBody?.isRoundTrip || orderFlights.length > 1);
  const flights = orderFlights.length
    ? orderFlights.map((flight, index) =>
        buildDefaultFlight({
          departureDate: formatSearchDate(flight?.departureDate) || startDate,
          arrivalDate: formatSearchDate(flight?.arrivalDate) || endDate,
          segmentIndex: flight?.segmentIndex ?? index,
          isReturn: isRoundTrip && index > 0,
        }),
      )
    : [
        buildDefaultFlight({
          departureDate: startDate,
          arrivalDate: endDate,
          segmentIndex: 0,
        }),
      ];

  const payload = {
    numberOfAdults:
      countPersonsByAge(persons, "adult") ||
      Number(requestBody?.numberOfAdults) ||
      1,
    numberOfChildren:
      countPersonsByAge(persons, "child") ||
      Number(requestBody?.numberOfChildren) ||
      0,
    numberOfInfants:
      countPersonsByAge(persons, "infant") ||
      Number(requestBody?.numberOfInfants) ||
      0,
    isRoundTrip,
    personInfo: buildInsurancePersonInfo(),
    destinationCountry: requestBody?.destinationCountry || "",
    startDate,
    endDate,
    purposeOfTravel: "Leisure",
    flights,
  };

  const supplier = requestBody?.supplier || booking?.supplier;
  if (supplier) payload.suppliers = [supplier];

  return payload;
}

function getSearchReq(booking, requestBodyOverride) {
  return (
    getRequestBody(booking)?.searchReq ||
    buildDocInsuranceSearchRequest(booking, requestBodyOverride)
  );
}

function getPolicyType(booking) {
  return (
    getRequestBody(booking)?.policyType ||
    booking?.orderData?.orderData?.policyType ||
    ""
  );
}

function matchPoliciesByType(policies, policyType) {
  const target = String(policyType || "")
    .trim()
    .toLowerCase();
  if (!target) return [];
  return (policies || []).filter(
    (policy) =>
      String(policy?.policyType || "")
        .trim()
        .toLowerCase() === target,
  );
}

function getPolicyId(policy) {
  return policy?.policyId || policy?.id || "";
}

function getPolicyAmount(policy) {
  return policy?.amount ?? policy?.farePrice ?? policy?.originalAmount;
}

export const RebookInsuranceBookingModal = ({
  setOpen,
  open,
  booking,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const { insuranceOrders } = useSelector((state) => state.order);
  const { searchInsurancePolicies, rebookInsurance } = useInsuranceApi();
  const cancelledRef = useRef(false);
  const editedRequestBodyRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("confirm");
  const [statusText, setStatusText] = useState(
    "Please wait while we find an updated policy",
  );
  const [matches, setMatches] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState("");

  const resetState = () => {
    setLoading(false);
    setPhase("confirm");
    setStatusText("Please wait while we find an updated policy");
    setMatches([]);
    setSelectedPolicyId("");
    editedRequestBodyRef.current = null;
  };

  const closeModal = () => {
    cancelledRef.current = true;
    resetState();
    setOpen(false);
  };

  const getActiveRequestBody = () =>
    editedRequestBodyRef.current || getRequestBody(booking);

  const submitRebook = async (policy, { keepOpenOnError } = {}) => {
    const requestBody = getActiveRequestBody();
    const newPolicyId = getPolicyId(policy);

    if (!requestBody || !newPolicyId) {
      enqueueSnackbar("Unable to rebook this insurance policy.", {
        variant: "error",
      });
      closeModal();
      return;
    }

    setLoading(true);
    setStatusText("Please wait while we rebook this policy");

    const success = await rebookInsurance({
      booking,
      payload: {
        ...requestBody,
        policyId: newPolicyId,
      },
      setLoading,
      callback: (data) => {
        if (cancelledRef.current) return;
        if (data?._id) {
          dispatch(
            setInsuranceOrders(
              insuranceOrders?.map((item) =>
                item?._id === data?._id ? data : item,
              ),
            ),
          );
        }
        onSuccess?.(data);
        closeModal();
      },
    });

    if (cancelledRef.current || success) return;
    if (!keepOpenOnError) closeModal();
  };

  const runSearch = async () => {
    const requestBody = getActiveRequestBody();
    const searchReq = getSearchReq(booking, editedRequestBodyRef.current);
    const policyType = getPolicyType(booking);

    if (!requestBody) {
      enqueueSnackbar("Unable to rebook this insurance policy.", {
        variant: "error",
      });
      closeModal();
      return;
    }

    if (!searchReq) {
      enqueueSnackbar("Unable to build a search request for this booking.", {
        variant: "error",
      });
      closeModal();
      return;
    }

    if (!policyType) {
      enqueueSnackbar("This booking has no policy type to match.", {
        variant: "error",
      });
      closeModal();
      return;
    }

    setLoading(true);
    setMatches([]);
    setSelectedPolicyId("");
    setStatusText("Please wait while we find an updated policy");

    const policies = await searchInsurancePolicies({ payload: searchReq });
    if (cancelledRef.current) return;

    if (!policies) {
      closeModal();
      return;
    }

    const matched = matchPoliciesByType(policies, policyType);
    if (!matched.length) {
      enqueueSnackbar(
        `No matching ${policyType} policy was found. Try again later.`,
        { variant: "warning" },
      );
      closeModal();
      return;
    }

    if (matched.length === 1) {
      await submitRebook(matched[0]);
      return;
    }

    setLoading(false);
    setMatches(matched);
    setSelectedPolicyId(getPolicyId(matched[0]));
    setPhase("picker");
  };

  useEffect(() => {
    if (!open) {
      cancelledRef.current = true;
      resetState();
      return;
    }

    cancelledRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?._id]);

  const handleConfirmRebook = async () => {
    setPhase("searching");
    cancelledRef.current = false;
    await runSearch();
  };

  const handleStartEdit = () => {
    if (!getRequestBody(booking)) {
      enqueueSnackbar("Unable to edit this insurance policy.", {
        variant: "error",
      });
      return;
    }
    setPhase("edit");
  };

  const handleContinueFromEdit = async (draft) => {
    editedRequestBodyRef.current = draft;
    setPhase("searching");
    cancelledRef.current = false;
    await runSearch();
  };

  const handleConfirmSelection = async () => {
    const policy = matches.find(
      (item) => getPolicyId(item) === selectedPolicyId,
    );
    if (!policy) {
      enqueueSnackbar("Select a policy to continue.", { variant: "warning" });
      return;
    }
    await submitRebook(policy, { keepOpenOnError: true });
  };

  const showPicker = open && phase === "picker" && !loading && matches.length > 1;
  const policyType = getPolicyType(booking);

  return (
    <>
      <Modal3
        open={open && phase === "confirm"}
        setOpen={(val) => {
          if (!val) closeModal();
        }}
        title="Rebook Insurance"
      >
        <div className="max-w-[500px]">
          <div className="p-4">
            <p>
              Are you sure you want to rebook this insurance policy? This action
              cannot be undone.
            </p>
            <button
              type="button"
              className="text-primary1 text-sm mt-3 underline"
              onClick={handleStartEdit}
            >
              Edit traveller details (optional)
            </button>
          </div>
          <div className="flex gap-4 p-4">
            <Button1 variant="text" type="button" onClick={closeModal}>
              Cancel
            </Button1>
            <Button1 type="button" onClick={handleConfirmRebook}>
              Confirm
            </Button1>
          </div>
        </div>
      </Modal3>
      <ModalContainer
        open={open && phase === "edit"}
        setOpen={closeModal}
        secondary
        padding="px-4 py-6"
        title="Edit traveller details"
        subTitle="Update traveller information before rebooking. This is optional."
      >
        {open && phase === "edit" ? (
          <RebookInsuranceDetailsForm
            requestBody={getRequestBody(booking)}
            onCancel={() => setPhase("confirm")}
            onContinue={handleContinueFromEdit}
          />
        ) : null}
      </ModalContainer>
      <ModalContainer
        open={open && (phase === "searching" || phase === "picker")}
        setOpen={closeModal}
        secondary
        padding="px-4 py-6"
        childClassname="max-w-[560px]"
        title="Rebook Insurance"
        subTitle={
          showPicker
            ? `Multiple ${policyType || "matching"} policies were found. Select the one to use.`
            : "Refreshing the policy before rebooking"
        }
      >
        {loading || !showPicker ? (
          <div className="w-full flex flex-col items-center py-6 gap-3">
            <img src="/preloader.gif" alt="" className="w-24 object-contain" />
            <p className="text-gray-600 text-center text-sm lg:text-base">
              {statusText}
            </p>
          </div>
        ) : (
          <div className="w-full flex flex-col gap-4">
            <div className="flex flex-col gap-3 max-h-[50vh] overflow-auto">
              {matches.map((policy, index) => {
                const policyId = getPolicyId(policy);
                const selected = selectedPolicyId === policyId;
                const amount = getPolicyAmount(policy);
                const currency = policy?.currency || booking?.currency;

                return (
                  <button
                    key={policyId || index}
                    type="button"
                    onClick={() => setSelectedPolicyId(policyId)}
                    className={`w-full text-left rounded-lg border p-4 flex items-start gap-3 ${
                      selected
                        ? "border-primary1 bg-[#F0F6FF]"
                        : "border-[#EFECEC]"
                    }`}
                  >
                    {String(policy?.logoURL || "").startsWith("http") ? (
                      <img
                        src={policy.logoURL}
                        alt=""
                        className="h-8 w-8 object-contain mt-0.5"
                      />
                    ) : (
                      <img
                        src="/IconInsurance2.svg"
                        alt=""
                        className="h-5 object-contain mt-1"
                      />
                    )}
                    <div className="flex flex-1 flex-col gap-1">
                      <span className="font-semibold text-sm lg:text-base">
                        {policy?.policyType || policyType || "Insurance policy"}
                      </span>
                      <span className="text-xs text-gray-500">
                        Policy ID: {policyId || "—"}
                      </span>
                      {policy?.supplier ? (
                        <span className="text-xs text-gray-500">
                          Supplier: {policy.supplier}
                        </span>
                      ) : null}
                      {policy?.description ? (
                        <span className="text-xs text-gray-500">
                          {policy.description}
                        </span>
                      ) : null}
                      {amount != null ? (
                        <span className="text-sm font-medium mt-1">
                          {formatMoney(amount, currency)}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-4 items-center">
              <button
                type="button"
                onClick={closeModal}
                className="py-4 px-6 text-xs md:text-lg font-bold w-full max-w-[174px]"
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-primary1 flex items-center justify-center text-white w-full py-4 rounded-lg text-xs sm:text-lg"
                onClick={handleConfirmSelection}
                disabled={!selectedPolicyId}
              >
                Use selected policy
              </button>
            </div>
          </div>
        )}
      </ModalContainer>
    </>
  );
};
